/**
 * VM API client service
 */

import axios from 'axios';
import { 
  VM, VMCreateRequest, VMListResponse, VMMetrics, VMSSHPasswordUpdate, VMSSHPasswordResponse,
  OSImage, OSImageListResponse,
  VMSnapshot, VMSnapshotCreate, VMSnapshotRestore, VMSnapshotListResponse,
  VMSnapshotSchedule, VMSnapshotScheduleCreate, VMSnapshotScheduleListResponse,
  CloudInitTemplate, CloudInitTemplateCreate, CloudInitTemplateUpdate,
  CloudInitTemplateListResponse, CloudInitTemplateValidateRequest, CloudInitTemplateValidateResponse,
  VMFirewallPort, VMFirewallPortCreate, VMFirewallPortUpdate, VMFirewallPortListResponse
} from '../types/vm';

const API_BASE_URL = process.env.REACT_APP_VM_MANAGER_API_URL || 'http://localhost:8000';

if (!process.env.REACT_APP_VM_MANAGER_API_URL) {
  console.warn('REACT_APP_VM_MANAGER_API_URL environment variable is not set. Using default: http://localhost:8000');
}

const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and logging interceptor (combined)
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Add project ID header if available
    const projectId = localStorage.getItem('selectedProjectId');
    if (projectId) {
      config.headers['X-Project-Id'] = projectId;
    }
    
    // Log request
    console.log('[vmApi] Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      data: config.data,
      headers: {
        Authorization: config.headers.Authorization ? 'Bearer ***' : 'none',
        'X-Project-Id': config.headers['X-Project-Id'] || 'none',
      },
    });
    
    return config;
  },
  (error) => {
    console.error('[vmApi] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error logging
apiClient.interceptors.response.use(
  (response) => {
    console.log('API Response:', {
      url: response.config.url,
      method: response.config.method,
      status: response.status,
      data: response.data,
    });
    return response;
  },
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      baseURL: error.config?.baseURL,
      fullURL: error.config ? `${error.config.baseURL}${error.config.url}` : 'unknown',
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      stack: error.stack,
    });
    return Promise.reject(error);
  }
);

export const vmApi = {
  /**
   * Create a new VM
   */
  async createVM(data: VMCreateRequest, projectId?: string): Promise<{ id: string; status: string; workflow_id: string; message: string }> {
    const headers: Record<string, string> = {};
    // Use project_id from data body, or fall back to projectId parameter for header
    const finalProjectId = data.project_id || projectId;
    if (finalProjectId) {
      headers['X-Project-Id'] = finalProjectId;
      // Also ensure it's in the body
      data.project_id = finalProjectId;
    }
    
    const response = await apiClient.post('/vms', data, { headers });
    return response.data;
  },

  /**
   * List VMs
   */
  async listVMs(params?: {
    project_id?: string;
    status?: string;
    page?: number;
    size?: number;
  }): Promise<VMListResponse> {
    // Filter out empty strings and undefined values to avoid validation errors
    const cleanParams: Record<string, string | number> = {};
    if (params?.project_id && params.project_id.trim() !== '') {
      cleanParams.project_id = params.project_id;
    }
    if (params?.status && params.status.trim() !== '') {
      cleanParams.status = params.status;
    }
    if (params?.page !== undefined && params.page !== null) {
      cleanParams.page = params.page;
    }
    if (params?.size !== undefined && params.size !== null) {
      cleanParams.size = params.size;
    }
    const response = await apiClient.get<VMListResponse>('/vms', { params: cleanParams });
    return response.data;
  },

  /**
   * Get VM by ID
   */
  async getVM(vmId: string): Promise<VM> {
    const response = await apiClient.get<VM>(`/vms/${vmId}`);
    return response.data;
  },

  /**
   * Start VM
   */
  async startVM(vmId: string): Promise<{ id: string; status: string; workflow_id: string; message: string }> {
    const response = await apiClient.post(`/vms/${vmId}/start`);
    return response.data;
  },

  /**
   * Stop VM
   */
  async stopVM(vmId: string): Promise<{ id: string; status: string; workflow_id: string; message: string }> {
    const response = await apiClient.post(`/vms/${vmId}/stop`);
    return response.data;
  },

  /**
   * Restart VM
   */
  async restartVM(vmId: string): Promise<{ id: string; status: string; workflow_id: string; message: string }> {
    const response = await apiClient.post(`/vms/${vmId}/restart`);
    return response.data;
  },

  /**
   * Delete VM
   */
  async deleteVM(vmId: string): Promise<{ id: string; status: string; workflow_id: string; message: string }> {
    const response = await apiClient.delete(`/vms/${vmId}`);
    return response.data;
  },

  /**
   * Get VM metrics
   */
  async getVMMetrics(vmId: string): Promise<VMMetrics> {
    const response = await apiClient.get<VMMetrics>(`/vms/${vmId}/metrics`);
    return response.data;
  },

  /**
   * Update SSH password
   */
  async updateSSHPassword(vmId: string, data: VMSSHPasswordUpdate): Promise<VMSSHPasswordResponse> {
    const response = await apiClient.put<VMSSHPasswordResponse>(`/vms/${vmId}/ssh-password`, data);
    return response.data;
  },

  /**
   * Retry VM creation (for VMs in ERROR state)
   */
  async retryVM(vmId: string): Promise<{ id: string; status: string; workflow_id: string; message: string }> {
    const response = await apiClient.post(`/vms/${vmId}/retry`);
    return response.data;
  },

  /**
   * Sync VM status with actual VM state
   * Useful when there's a mismatch between stored status and actual VM state
   */
  async syncVMStatus(vmId: string): Promise<{
    vm_id: string;
    database_status: string;
    previous_status?: string;
    actual_status: {
      state?: string;
      phase?: string;
      running: boolean;
      ready: boolean;
    };
    synced: boolean;
    message: string;
  }> {
    const response = await apiClient.post(`/vms/${vmId}/sync-status`);
    return response.data;
  },

  /**
   * List available OS images
   */
  async listOSImages(category?: string, availableOnly: boolean = true): Promise<OSImageListResponse> {
    const params: Record<string, string | boolean> = { available_only: availableOnly };
    if (category) {
      params.category = category;
    }
    const response = await apiClient.get<OSImageListResponse>('/os-images', { params });
    return response.data;
  },

  /**
   * Get OS image details by name
   */
  async getOSImage(imageName: string): Promise<OSImage> {
    const response = await apiClient.get<OSImage>(`/os-images/${imageName}`);
    return response.data;
  },

  /**
   * Create a VM snapshot
   */
  async createSnapshot(vmId: string, data: VMSnapshotCreate): Promise<{ id: string; vm_id: string; name: string; status: string; workflow_id: string; message: string }> {
    const response = await apiClient.post(`/vms/${vmId}/snapshots`, data);
    return response.data;
  },

  /**
   * List snapshots for a VM
   */
  async listVMSnapshots(vmId: string): Promise<VMSnapshotListResponse> {
    const response = await apiClient.get<VMSnapshotListResponse>(`/vms/${vmId}/snapshots`);
    return response.data;
  },

  /**
   * List all snapshots
   */
  async listSnapshots(params?: { vm_id?: string; project_id?: string; status?: string }): Promise<VMSnapshotListResponse> {
    const response = await apiClient.get<VMSnapshotListResponse>('/snapshots', { params });
    return response.data;
  },

  /**
   * Get snapshot details
   */
  async getSnapshot(snapshotId: string): Promise<VMSnapshot> {
    const response = await apiClient.get<VMSnapshot>(`/snapshots/${snapshotId}`);
    return response.data;
  },

  /**
   * Restore from a snapshot
   */
  async restoreSnapshot(snapshotId: string, data: VMSnapshotRestore): Promise<{ snapshot_id: string; vm_id: string; mode: string; workflow_id: string; message: string }> {
    const response = await apiClient.post(`/snapshots/${snapshotId}/restore`, data);
    return response.data;
  },

  /**
   * Delete a snapshot
   */
  async deleteSnapshot(snapshotId: string): Promise<void> {
    await apiClient.delete(`/snapshots/${snapshotId}`);
  },

  /**
   * Create a snapshot schedule
   */
  async createSnapshotSchedule(vmId: string, data: VMSnapshotScheduleCreate): Promise<VMSnapshotSchedule> {
    const response = await apiClient.post<VMSnapshotSchedule>(`/vms/${vmId}/snapshot-schedules`, data);
    return response.data;
  },

  /**
   * List snapshot schedules for a VM
   */
  async listSnapshotSchedules(vmId: string): Promise<VMSnapshotScheduleListResponse> {
    const response = await apiClient.get<VMSnapshotScheduleListResponse>(`/vms/${vmId}/snapshot-schedules`);
    return response.data;
  },

  /**
   * Update a snapshot schedule
   */
  async updateSnapshotSchedule(scheduleId: string, data: Partial<VMSnapshotScheduleCreate> & { is_enabled?: boolean }): Promise<VMSnapshotSchedule> {
    const response = await apiClient.put<VMSnapshotSchedule>(`/snapshot-schedules/${scheduleId}`, data);
    return response.data;
  },

  /**
   * Delete a snapshot schedule
   */
  async deleteSnapshotSchedule(scheduleId: string): Promise<void> {
    await apiClient.delete(`/snapshot-schedules/${scheduleId}`);
  },

  async estimateVMCost(data: {
    cpu_cores: number;
    memory_gb: number;
    storage_gb: number;
    gpu_count?: number;
    gpu_type?: string;
    gpu_memory_gb?: number;
    billing_period: string;
    use_case?: string;
    is_spot?: boolean;
  }): Promise<import('../types/vm').VMCostEstimate> {
    const response = await apiClient.post('/vms/billing/estimate', data);
    return response.data;
  },

  /**
   * Get WebSocket URL for VM terminal
   */
  getTerminalUrl(vmId: string, projectId?: string): string {
    const token = localStorage.getItem('token');
    // Determine WebSocket protocol based on API_BASE_URL first, then fallback to window.location
    let wsProtocol = 'ws:';
    if (API_BASE_URL.startsWith('https://')) {
      wsProtocol = 'wss:';
    } else if (API_BASE_URL.startsWith('http://')) {
      wsProtocol = 'ws:';
    } else if (window.location.protocol === 'https:') {
      wsProtocol = 'wss:';
    }
    
    // Extract host from API_BASE_URL, removing protocol and trailing slashes
    const wsHost = API_BASE_URL.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const params = new URLSearchParams({ token: token || '' });
    if (projectId) {
      params.append('project_id', projectId);
    }
    return `${wsProtocol}//${wsHost}/api/v1/vms/${vmId}/terminal?${params.toString()}`;
  },

  /**
   * Get WebSocket URL for VM logs stream
   */
  getLogsStreamUrl(vmId: string, projectId?: string, tail: number = 100, follow: boolean = true): string {
    const token = localStorage.getItem('token');
    // Determine WebSocket protocol based on API_BASE_URL first, then fallback to window.location
    let wsProtocol = 'ws:';
    if (API_BASE_URL.startsWith('https://')) {
      wsProtocol = 'wss:';
    } else if (API_BASE_URL.startsWith('http://')) {
      wsProtocol = 'ws:';
    } else if (window.location.protocol === 'https:') {
      wsProtocol = 'wss:';
    }
    
    // Extract host from API_BASE_URL, removing protocol and trailing slashes
    const wsHost = API_BASE_URL.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const params = new URLSearchParams({ 
      token: token || '',
      tail: tail.toString(),
      follow: follow.toString()
    });
    if (projectId) {
      params.append('project_id', projectId);
    }
    return `${wsProtocol}//${wsHost}/api/v1/vms/${vmId}/logs/stream?${params.toString()}`;
  },

  /**
   * Get VM logs (REST endpoint)
   */
  async getVMLogs(vmId: string, tail: number = 100): Promise<{ vm_id: string; pod_name: string; logs: string[]; tail_lines: number }> {
    const response = await apiClient.get(`/vms/${vmId}/logs`, { params: { tail } });
    return response.data;
  },

  /**
   * Resize VM (update CPU and memory)
   */
  async resizeVM(vmId: string, data: { cpu_cores: number; memory_gb: number }): Promise<{ id: string; status: string; workflow_id: string; message: string; new_cpu_cores: number; new_memory_gb: number }> {
    const response = await apiClient.patch(`/vms/${vmId}/resize`, data);
    return response.data;
  },

  /**
   * Firewall Port Management
   */
  async getVMFirewallPorts(vmId: string): Promise<VMFirewallPortListResponse> {
    const response = await apiClient.get<VMFirewallPortListResponse>(`/vms/${vmId}/firewall/ports`);
    return response.data;
  },

  async addVMFirewallPort(vmId: string, portConfig: VMFirewallPortCreate): Promise<VMFirewallPort> {
    const response = await apiClient.post<VMFirewallPort>(`/vms/${vmId}/firewall/ports`, portConfig);
    return response.data;
  },

  async updateVMFirewallPort(vmId: string, portId: string, updates: VMFirewallPortUpdate): Promise<VMFirewallPort> {
    const response = await apiClient.put<VMFirewallPort>(`/vms/${vmId}/firewall/ports/${portId}`, updates);
    return response.data;
  },

  async deleteVMFirewallPort(vmId: string, portId: string): Promise<void> {
    await apiClient.delete(`/vms/${vmId}/firewall/ports/${portId}`);
  },

  async bulkUpdateVMFirewallPorts(vmId: string, ports: VMFirewallPortCreate[]): Promise<VMFirewallPortListResponse> {
    const response = await apiClient.patch<VMFirewallPortListResponse>(`/vms/${vmId}/firewall/ports`, { ports });
    return response.data;
  },
};

// VM Pool API client
export const vmPoolApi = {
  /**
   * Create a new VM Pool
   */
  async createPool(data: any, projectId?: string): Promise<{ id: string; status: string; workflow_id: string; message: string }> {
    const headers: Record<string, string> = {};
    const finalProjectId = data.project_id || projectId;
    if (finalProjectId) {
      headers['X-Project-Id'] = finalProjectId;
      data.project_id = finalProjectId;
    }
    const response = await apiClient.post('/vm-pools', data, { headers });
    return response.data;
  },

  /**
   * List VM Pools
   */
  async listPools(params?: {
    project_id?: string;
    page?: number;
    size?: number;
  }): Promise<any> {
    const cleanParams: Record<string, string | number> = {};
    if (params?.project_id && params.project_id.trim() !== '') {
      cleanParams.project_id = params.project_id;
    }
    if (params?.page !== undefined && params.page !== null) {
      cleanParams.page = params.page;
    }
    if (params?.size !== undefined && params.size !== null) {
      cleanParams.size = params.size;
    }
    const response = await apiClient.get('/vm-pools', { params: cleanParams });
    return response.data;
  },

  /**
   * Get VM Pool by ID
   */
  async getPool(poolId: string): Promise<any> {
    const response = await apiClient.get(`/vm-pools/${poolId}`);
    return response.data;
  },

  /**
   * Update VM Pool
   */
  async updatePool(poolId: string, data: any): Promise<any> {
    const response = await apiClient.patch(`/vm-pools/${poolId}`, data);
    return response.data;
  },

  /**
   * Scale VM Pool
   */
  async scalePool(poolId: string, replicas: number): Promise<{ id: string; status: string; workflow_id: string; message: string }> {
    const response = await apiClient.patch(`/vm-pools/${poolId}/scale`, { replicas });
    return response.data;
  },

  /**
   * Delete VM Pool
   */
  async deletePool(poolId: string): Promise<{ id: string; status: string; workflow_id: string; message: string }> {
    const response = await apiClient.delete(`/vm-pools/${poolId}`);
    return response.data;
  },
};

// Data Volume API client
export const dataVolumeApi = {
  /**
   * Create a new Data Volume
   */
  async createVolume(data: any, projectId?: string): Promise<{ id: string; status: string; workflow_id: string; message: string }> {
    const headers: Record<string, string> = {};
    const finalProjectId = data.project_id || projectId;
    if (finalProjectId) {
      headers['X-Project-Id'] = finalProjectId;
    }
    // Remove project_id from body as it's passed as query parameter
    const { project_id, ...bodyData } = data;
    const params: Record<string, string> = {};
    if (finalProjectId) {
      params.project_id = finalProjectId;
    }
    const response = await apiClient.post('/data-volumes', bodyData, { headers, params });
    return response.data;
  },

  /**
   * List Data Volumes
   */
  async listVolumes(params?: {
    project_id?: string;
    page?: number;
    size?: number;
  }): Promise<any> {
    const cleanParams: Record<string, string | number> = {};
    if (params?.project_id && params.project_id.trim() !== '') {
      cleanParams.project_id = params.project_id;
    }
    if (params?.page !== undefined && params.page !== null) {
      cleanParams.page = params.page;
    }
    if (params?.size !== undefined && params.size !== null) {
      cleanParams.size = params.size;
    }
    const response = await apiClient.get('/data-volumes', { params: cleanParams });
    return response.data;
  },

  /**
   * Get Data Volume by ID
   */
  async getVolume(volumeId: string): Promise<any> {
    const response = await apiClient.get(`/data-volumes/${volumeId}`);
    return response.data;
  },

  /**
   * Delete Data Volume
   */
  async deleteVolume(volumeId: string): Promise<{ id: string; status: string; workflow_id: string; message: string }> {
    const response = await apiClient.delete(`/data-volumes/${volumeId}`);
    return response.data;
  },
};

// Cloud-init Template API client
export const cloudInitTemplateApi = {
  /**
   * List cloud-init templates
   */
  async listTemplates(params?: {
    project_id?: string;
    include_public?: boolean;
  }): Promise<CloudInitTemplateListResponse> {
    const cleanParams: Record<string, string | boolean> = {};
    if (params?.project_id && params.project_id.trim() !== '') {
      cleanParams.project_id = params.project_id;
    }
    if (params?.include_public !== undefined) {
      cleanParams.include_public = params.include_public;
    }
    const response = await apiClient.get<CloudInitTemplateListResponse>('/cloudinit-templates', { params: cleanParams });
    return response.data;
  },

  /**
   * Get cloud-init template by ID
   */
  async getTemplate(templateId: string): Promise<CloudInitTemplate> {
    const response = await apiClient.get<CloudInitTemplate>(`/cloudinit-templates/${templateId}`);
    return response.data;
  },

  /**
   * Create a new cloud-init template
   */
  async createTemplate(data: CloudInitTemplateCreate): Promise<CloudInitTemplate> {
    const response = await apiClient.post<CloudInitTemplate>('/cloudinit-templates', data);
    return response.data;
  },

  /**
   * Update cloud-init template
   */
  async updateTemplate(templateId: string, data: CloudInitTemplateUpdate): Promise<CloudInitTemplate> {
    const response = await apiClient.put<CloudInitTemplate>(`/cloudinit-templates/${templateId}`, data);
    return response.data;
  },

  /**
   * Delete cloud-init template
   */
  async deleteTemplate(templateId: string): Promise<void> {
    await apiClient.delete(`/cloudinit-templates/${templateId}`);
  },

  /**
   * Validate cloud-init configuration
   */
  async validateConfig(data: CloudInitTemplateValidateRequest): Promise<CloudInitTemplateValidateResponse> {
    const response = await apiClient.post<CloudInitTemplateValidateResponse>('/cloudinit-templates/validate', data);
    return response.data;
  },
};

